import React, {forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'

import {Input} from './index.js'
import {InputChangeEvent, InputRef} from './Input.js'
import {formatPrice} from '../../../../utils/price.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'

export interface CurrencyInputProps extends FieldProps, InvalidProps {
    value?: string | number
    // Only called on blur and Enter, with the value formatted as a price ('12,50')
    onChange?: (event: {target: {value: string}}) => void
    id?: string
    prepend?: React.ReactNode
    append?: React.ReactNode
}

export type CurrencyInputRef = FieldRef

const CurrencyInput = forwardRef<CurrencyInputRef, CurrencyInputProps>(function CurrencyInput(props, ref) {
    const {style, id, label, info, prepend = '€', append, isInvalid, disabled, readOnly} = props

    const [value, setValue] = useState<string>(typeof props.value === 'number' ? formatPrice(props.value) : (props.value))

    const formControlRef = useRef<InputRef | null>(null)
    const prevValueRef = useRef(props.value)
    // True after Enter committed the value, so the blur that follows doesn't call onChange again for the same text
    const committedOnEnterRef = useRef(false)

    useImperativeHandle(ref, () => ({
        focus: () => formControlRef.current?.focus(),
        blur: () => formControlRef.current?.blur(),
        setInvalid: (isInvalid = true) => formControlRef.current?.setInvalid(isInvalid)
    }), [])

    useLayoutEffect(() => {
        const prevValue = prevValueRef.current
        prevValueRef.current = props.value

        if (prevValue !== props.value) {
            setValue(typeof props.value === 'number' ? formatPrice(props.value) : props.value)
        }
    }, [props.value])

    const commit = () => {
        // A readOnly field can still be focused, leaving it or pressing Enter must not reformat or report the value
        if (disabled || readOnly) {
            return
        }

        // The typing regex allows partial input formatPrice doesn't parse (and would return unchanged), complete it first:
        // '12,' -> '12', ',5' -> '0,5', and a lone '-' (or ',') -> '' which formatPrice turns into '0,00' like an empty field
        const text = (value ?? '').replace(/,$/, '').replace(/^(-?),/, (match, sign) => `${sign}0,`).replace(/^-$/, '')
        const event = {target: {value: formatPrice(text)}}

        setValue(event.target.value)

        // Was called in the setState callback; the state update and this call are batched in the same event
        props.onChange?.(event)
    }

    const onBlur = () => {
        if (committedOnEnterRef.current) {
            committedOnEnterRef.current = false
            return
        }

        commit()
    }

    // Commit on Enter too: a parent that confirms on Enter (e.g. Popup) would otherwise read the value from before typing.
    // This runs in React's keydown handler, so the parent's state update is flushed before Popup's document listener runs.
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commit()
            committedOnEnterRef.current = true
        }
    }

    const onChange = (event: InputChangeEvent) => {
        event.target.value = event.target.value?.replace('.', ',')

        if (/^$|^[-]{0,1}[0-9]{0,5}$|^[-]{0,1}[0-9]{0,5}[,][0-9]{0,2}$/.test(event.target.value)) {
            setValue(event.target.value)
            committedOnEnterRef.current = false
        }
    }

    return (
        <Input
            id={id}
            style={style}
            label={label}
            info={info}
            value={value}
            isInvalid={isInvalid}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onChange={onChange}
            disabled={disabled}
            readOnly={readOnly}
            prepend={prepend}
            append={append}
            ref={formControlRef}
        />
    )
})

export default CurrencyInput
